import {
    Box,
    ButtonBase,
    Fade,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Typography,
} from "@mui/material";
import {alpha} from "@mui/material/styles";
import {MouseEvent, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import PublicIcon from "@mui/icons-material/Public";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckIcon from "@mui/icons-material/Check";

const languages = [
    {
        code: "ru",
        shortLabel: "RU",
        label: "Русский",
    },
    {
        code: "kk",
        shortLabel: "KZ",
        label: "Қазақша",
    },
    {
        code: "en",
        shortLabel: "EN",
        label: "English",
    },
] as const;

const LanguageSwitcher = () => {
    const {i18n} = useTranslation();

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const currentCode = (i18n.resolvedLanguage || i18n.language || "ru").split("-")[0];

    const currentLanguage = useMemo(() => {
        return languages.find((language) => language.code === currentCode) || languages[0];
    }, [currentCode]);

    const handleOpen = (event: MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleChangeLanguage = (languageCode: string) => {
        i18n.changeLanguage(languageCode);
        handleClose();
    };

    return (
        <>
            <ButtonBase
                onClick={handleOpen}
                aria-controls={open ? "language-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
                sx={{
                    height: 36,
                    px: {xs: 1, sm: 1.25},
                    gap: 0.75,
                    borderRadius: 2,
                    color: "#ffffff",
                    border: "1px solid rgba(255,255,255,0.14)",
                    bgcolor: open ? "rgba(255,255,255,0.12)" : "rgba(255,255,255,0.06)",
                    transition: "background-color 160ms ease, border-color 160ms ease",
                    "&:hover": {
                        bgcolor: "rgba(255,255,255,0.10)",
                        borderColor: "rgba(255,255,255,0.24)",
                    },
                }}
            >
                <Box
                    sx={{
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        display: {xs: "none", sm: "flex"},
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "rgba(255,255,255,0.10)",
                    }}
                >
                    <PublicIcon sx={{fontSize: 15}}/>
                </Box>

                <Typography
                    sx={{
                        fontSize: 12,
                        fontWeight: 800,
                        letterSpacing: "0.3px",
                        lineHeight: 1,
                    }}
                >
                    {currentLanguage.shortLabel}
                </Typography>

                <KeyboardArrowDownIcon
                    sx={{
                        fontSize: 18,
                        opacity: 0.75,
                        transform: open ? "rotate(180deg)" : "rotate(0deg)",
                        transition: "transform 160ms ease",
                    }}
                />
            </ButtonBase>

            <Menu
                id="language-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                slots={{
                    transition: Fade,
                }}
                slotProps={{
                    paper: {
                        sx: {
                            mt: 1,
                            minWidth: 180,
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: "divider",
                            boxShadow: "0 14px 35px rgba(15,23,42,0.18)",
                            overflow: "hidden",
                            "& .MuiList-root": {
                                py: 0.75,
                            },
                        },
                    },
                }}
            >
                <Box sx={{px: 1.5, pt: 0.7, pb: 0.5}}>
                    <Typography
                        sx={{
                            fontSize: 11,
                            fontWeight: 800,
                            color: "text.secondary",
                            textTransform: "uppercase",
                            letterSpacing: "0.7px",
                        }}
                    >
                        Language
                    </Typography>
                </Box>

                {languages.map((language) => {
                    const selected = language.code === currentLanguage.code;

                    return (
                        <MenuItem
                            key={language.code}
                            selected={selected}
                            onClick={() => handleChangeLanguage(language.code)}
                            sx={(theme) => ({
                                mx: 0.75,
                                my: 0.25,
                                px: 1,
                                borderRadius: 1.5,
                                minHeight: 40,
                                gap: 1,
                                bgcolor: selected
                                    ? alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.18 : 0.08)
                                    : "transparent",
                                "&.Mui-selected": {
                                    bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.18 : 0.08),
                                },
                                "&.Mui-selected:hover": {
                                    bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.24 : 0.12),
                                },
                                "&:hover": {
                                    bgcolor: theme.palette.action.hover,
                                },
                            })}
                        >
                            <ListItemIcon sx={{minWidth: 32}}>
                                <Box
                                    sx={(theme) => ({
                                        width: 28,
                                        height: 28,
                                        borderRadius: 1.5,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: 11,
                                        fontWeight: 900,
                                        color: selected
                                            ? theme.palette.primary.main
                                            : theme.palette.text.secondary,
                                        bgcolor: selected
                                            ? alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.16 : 0.08)
                                            : theme.palette.action.hover,
                                    })}
                                >
                                    {language.shortLabel}
                                </Box>
                            </ListItemIcon>

                            <ListItemText
                                primary={language.label}
                                slotProps={{
                                    primary: {
                                        sx: {
                                            fontSize: 14,
                                            fontWeight: selected ? 800 : 600,
                                        },
                                    },
                                }}
                            />

                            {selected && (
                                <CheckIcon
                                    sx={{
                                        fontSize: 18,
                                        color: "primary.main",
                                    }}
                                />
                            )}
                        </MenuItem>
                    );
                })}
            </Menu>
        </>
    );
};

export default LanguageSwitcher;